"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const material_1 = require("@mui/material");
const react_query_1 = require("@tanstack/react-query");
const lexico_1 = __importDefault(require("@tradeshift/lexico"));
const lodash_1 = __importDefault(require("lodash"));
const react_1 = require("react");
const react_router_1 = require("react-router");
const uuid_1 = require("uuid");
const empty_wallet_state_illustration_svg_1 = __importDefault(require("../../../../../../assets/img/empty-wallet-state-illustration.svg"));
const HSBC_Hand_credit_card_HBSC_svg_1 = __importDefault(require("../../../../../../assets/img/hsbc/HSBC_Hand-credit-card-HBSC.svg"));
const appConstants_1 = require("../../../../constants/appConstants");
const uiConstants_1 = require("../../../../constants/uiConstants");
const companyQuery_1 = require("../../../../queries/companyQuery");
const query_keys_1 = require("../../../../queries/query-keys");
const requestListQuery_1 = require("../../../../queries/requestListQuery");
const teamQuery_1 = require("../../../../queries/teamQuery");
const userQuery_1 = require("../../../../queries/userQuery");
const Root_1 = require("../../../../router/Root");
const zustandStore_1 = require("../../../../zustandStore");
const simpleButton_1 = __importDefault(require("../../../atoms/button/simpleButton"));
const lexico = new lexico_1.default();
const EmptyWallet = () => {
    const theme = (0, material_1.useTheme)();
    const { userRole } = (0, react_router_1.useLoaderData)();
    const { data: userData } = (0, react_query_1.useQuery)(userQuery_1.userQuery);
    const { data: companyPropertiesData } = (0, react_query_1.useQuery)((0, companyQuery_1.companyPropertiesQuery)(userData?.companyAccountId, {
        cardprovider: 'tsgo-cardprovider',
        defaultExpirationDate: 'tsgo-virtualcardexprange',
    }));
    const cardProvider = companyPropertiesData?.cardprovider;
    const isHsbcCardProvider = cardProvider === 'HSBC';
    const { wallet, user, setPageLoading } = (0, zustandStore_1.useZustand)(state => ({
        ...state,
    }));
    const { cards, cardStatusFilter, teamFilter, memberFilter } = wallet;
    const { userId } = user;
    const noWalletImgPath = isHsbcCardProvider ? HSBC_Hand_credit_card_HBSC_svg_1.default : empty_wallet_state_illustration_svg_1.default;
    const navigate = (0, react_router_1.useNavigate)();
    const [isNewRequestLoading, setIsNewRequestLoading] = (0, react_1.useState)(false);
    const { data: teamId } = (0, react_query_1.useQuery)((0, teamQuery_1.selectedTeamIdQuery)('selectedTeamID'));
    const { data: requests, isFetching: isGetRequestsFetching } = (0, requestListQuery_1.useGetRequestListQuery)();
    const registerRequest = (0, requestListQuery_1.useRegisterRequestMutation)();
    const [emptyMessage, setEmptyMessage] = (0, react_1.useState)('');
    (0, react_1.useEffect)(() => {
        let text = '';
        const cardStatus = cards?.length === 0 ? '' : cardStatusFilter + ' ';
        if (teamFilter === memberFilter || (teamFilter !== 'all' && memberFilter === userId)) {
            text = `No ${cardStatus}cards in your wallet`;
        }
        else if (memberFilter === 'all' && teamFilter !== 'all') {
            text = `No ${cardStatus}cards for this team`;
        }
        else if (memberFilter !== 'all' && memberFilter !== userId) {
            text = `No ${cardStatus}cards for this team member`;
        }
        else {
            text = `No ${cardStatus}cards in your wallet`;
        }
        setEmptyMessage(lexico.trc('Empty wallet list', text));
    }, [teamFilter, memberFilter, cardStatusFilter, userId, cards?.length]);
    /**
     * Supplies a new request id and navigates to request creation page
     */
    const createRequest = async (e) => {
        e?.preventDefault();
        setPageLoading(true);
        if (isNewRequestLoading || isGetRequestsFetching) {
            setPageLoading(false);
            return;
        }
        setIsNewRequestLoading(true);
        const createdRequest = Array.isArray(requests)
            ? requests.find(request => {
                if (teamId?.[0]) {
                    return request.state === appConstants_1.requestStatus.CREATED && request.teamId === teamId?.[0];
                }
                return request.state === appConstants_1.requestStatus.CREATED;
            })
            : { requestId: '' };
        if (!lodash_1.default.isEmpty(createdRequest) && createdRequest.requestId) {
            void navigate(`/requests/${createdRequest.requestId}`);
            setIsNewRequestLoading(false);
            setPageLoading(false);
        }
        else {
            const requestId = (0, uuid_1.v4)();
            registerRequest.mutate({ requestId }, {
                onSuccess: () => {
                    void navigate(`/requests/${requestId}`);
                },
                onSettled: async () => {
                    setPageLoading(false);
                    setIsNewRequestLoading(false);
                    await Root_1.queryClient.invalidateQueries({
                        queryKey: query_keys_1.requestKeys.getRequestList(),
                    });
                },
            });
        }
    };
    return ((0, jsx_runtime_1.jsx)(material_1.Box, { sx: [
            {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                margin: 'auto',
                width: '450px',
                paddingTop: theme.tsGoUnit.tsUnitDouble,
            },
            globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                ? {
                    paddingTop: 0,
                }
                : {},
        ], children: [appConstants_1.supportAdmin, appConstants_1.companyAdmin].includes(userRole) || (cards?.length === 0 && memberFilter !== userId) ? null : ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("img", { src: noWalletImgPath, alt: "", "data-testid": "empty-wallet-image" }), (0, jsx_runtime_1.jsx)("h2", { "data-testid": "empty-wallet-header", children: emptyMessage }), (0, jsx_runtime_1.jsx)(material_1.Typography, { "ts-variant": "empty-state", "data-testid": "empty-wallet-sub-header", style: {
                        ...(globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                            ? {
                                marginTop: 0,
                                marginBottom: theme.tsGoUnit.tsUnit,
                            }
                            : {}),
                    }, children: isHsbcCardProvider
                        ? 'You have no virtual cards in your wallet. Create a request to start using HSBC Business Virtual Cards'
                        : lexico.trc('Empty wallet list', 'You have no virtual cards in your wallet. Create a request to start using Tradeshift Go cards') }), (0, jsx_runtime_1.jsx)(simpleButton_1.default, { dataTestId: "create-request", onClick: createRequest, color: uiConstants_1.UI_BUTTON_PRIMARY, style: { marginTop: 10, width: '50%' }, label: lexico.trc('Button info', 'Create a request') })] })) }));
};
exports.default = EmptyWallet;
